import React, { useState } from 'react';
import { MessageSquare, Send, Search, Loader2, User, ListChecks } from 'lucide-react';
import { useConversations, useMessages, useSendMessage, useMarkMessageAsRead } from '../../services/messageService';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const VendorMessages = () => {
  const { user } = useAuth();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: conversationsData, isLoading: conversationsLoading } = useConversations();
  // Ensure conversations is always an array
  const conversations = Array.isArray(conversationsData?.data)
    ? conversationsData.data
    : Array.isArray(conversationsData)
      ? conversationsData
      : [];

  const { data: messagesData, isLoading: messagesLoading } = useMessages(selectedConversation?.id);
  // Ensure messages is always an array
  const messages = Array.isArray(messagesData?.data)
    ? messagesData.data
    : Array.isArray(messagesData)
      ? messagesData
      : [];

  const sendMessageMutation = useSendMessage({
    onSuccess: () => {
      setMessageText('');
    },
  });

  const markAsReadMutation = useMarkMessageAsRead();

  const filteredConversations = Array.isArray(conversations)
    ? conversations.filter((conv) => {
        const otherUser = conv.participants?.find((p) => p.id !== user?.id);
        const searchLower = searchTerm.toLowerCase();
        return (
          !searchTerm ||
          conv.last_message?.toLowerCase().includes(searchLower) ||
          otherUser?.name?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    // Mark messages as read when opening conversation
    if (conversation.unread_count > 0) {
      // Mark all unread messages as read
      messages.forEach((msg) => {
        if (!msg.is_read && msg.sender_id !== user?.id) {
          markAsReadMutation.mutate(msg.id);
        }
      });
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation) return;

    sendMessageMutation.mutate({
      conversationId: selectedConversation.id,
      message: messageText.trim(),
    });
  };

  const selectedOtherUser = selectedConversation?.participants?.find((p) => p.id !== user?.id);

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Messages"
        subtitle="Communicate with your clients about upcoming events."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.messages.title}
        steps={VENDOR_PAGE_GUIDES.messages.steps}
        icon={ListChecks}
      />

      {conversationsLoading ? (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
          <div className="py-20 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading conversations…</p>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
          <div className="flex flex-col lg:flex-row h-[calc(100vh-14rem)] min-h-[28rem]">
            {/* Conversations list */}
            <div className="w-full lg:w-1/3 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0">
              <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 !rounded-xl"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                {filteredConversations.length === 0 ? (
                  <div className="py-14 px-4 text-center">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                      <MessageSquare className="w-7 h-7" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                      No conversations
                    </h3>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                      {searchTerm
                        ? 'No conversations match your search.'
                        : 'When clients message you, threads will appear here.'}
                    </p>
                  </div>
                ) : (
                  filteredConversations.map((conversation) => {
                    const otherUser = conversation.participants?.find((p) => p.id !== user?.id);
                    const isSelected = selectedConversation?.id === conversation.id;

                    return (
                      <button
                        key={conversation.id}
                        type="button"
                        onClick={() => handleSelectConversation(conversation)}
                        className={`w-full text-left p-4 border-b border-gray-200 dark:border-gray-800 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                          isSelected
                            ? 'bg-gray-50 dark:bg-gray-800/80'
                            : 'bg-transparent'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-700">
                            {otherUser?.profile_image_url ? (
                              <img
                                src={otherUser.profile_image_url}
                                alt={otherUser.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                                {otherUser?.name || 'Unknown User'}
                              </h3>
                              {conversation.unread_count > 0 && (
                                <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[11px] font-medium rounded-full bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
                                  {conversation.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {conversation.last_message || 'No messages yet'}
                            </p>
                            {conversation.last_message_time && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {new Date(conversation.last_message_time).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Messages pane */}
            <div className="flex-1 flex flex-col min-h-0 bg-white dark:bg-gray-900">
              {selectedConversation ? (
                <>
                  <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden border border-gray-200 dark:border-gray-700">
                        {selectedOtherUser?.profile_image_url ? (
                          <img
                            src={selectedOtherUser.profile_image_url}
                            alt={selectedOtherUser.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                          {selectedOtherUser?.name || 'Unknown User'}
                        </h3>
                        {selectedOtherUser?.email ? (
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {selectedOtherUser.email}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 min-h-0">
                    {messagesLoading ? (
                      <div className="flex flex-col justify-center items-center h-full py-16 text-center">
                        <Loader2 className="w-7 h-7 animate-spin text-primary-600 mb-3" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading messages…</p>
                      </div>
                    ) : messages.length === 0 ? (
                      <div className="flex flex-col justify-center items-center h-full py-16 text-center px-4">
                        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                          <MessageSquare className="w-7 h-7" />
                        </div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                          No messages yet
                        </h3>
                        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
                          Start the conversation — your reply will appear here.
                        </p>
                      </div>
                    ) : (
                      messages.map((message) => {
                        const isOwnMessage = message.sender_id === user?.id;
                        return (
                          <div
                            key={message.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3.5 py-2.5 rounded-2xl ${
                                isOwnMessage
                                  ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                                  : 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
                              }`}
                            >
                              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                {message.message}
                              </p>
                              <p
                                className={`text-[11px] mt-1.5 ${
                                  isOwnMessage
                                    ? 'text-gray-400 dark:text-gray-500'
                                    : 'text-gray-500 dark:text-gray-400'
                                }`}
                              >
                                {new Date(message.created_at).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="sticky bottom-0 flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
                    <form onSubmit={handleSendMessage} className="flex gap-2 items-stretch">
                      <Input
                        type="text"
                        placeholder="Type a message..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        className="flex-1 !rounded-xl"
                      />
                      <Button
                        type="submit"
                        icon={Send}
                        disabled={!messageText.trim() || sendMessageMutation.isPending}
                        loading={sendMessageMutation.isPending}
                        className="!rounded-xl"
                      >
                        Send
                      </Button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center px-4">
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400">
                      <MessageSquare className="w-7 h-7" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
                      Select a conversation
                    </h3>
                    <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                      Choose a thread from the left to read and reply to client messages.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorMessages;
